<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { ConnectedAccountsEmbed } from '@/Pejer/tables/ConnectedAccounts'
import { ChevronDown } from 'lucide-vue-next'
import { Button } from 'pejer/src/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'pejer/src/components/ui/dropdown-menu'
import DirectPaymentAccountOnboardingModal from '../DirectPaymentAccount/DirectPaymentAccountOnboardingModal.vue'
import ArchiveAccountModal from './ArchiveAccountModal.vue'
import DirectRemittanceUpgradeModal from './DirectPayment/DirectPaymentUpgradeModal.vue'
import PersonOfInterestModal from './PersonOfInterestModal.vue'

defineProps<{
  connectedAccount: ConnectedAccountsEmbed.ListItem
}>()

const pejer = usePejer()
</script>
<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" class="h-8 hover:bg-slate-200">
        <span>Valg</span>
        <ChevronDown class="w-4 h-4 shrink-0 ml-1" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuLabel>Valg</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuGroup
        :class="{
          'text-red-500 font-semibold': !connectedAccount.payoutsEnabled,
        }"
      >
        <DropdownMenuItem class="p-0">
          <PersonOfInterestModal :connectedAccount="connectedAccount">
            <Button
              @click.prevent.stop
              class="p-2 rounded-sm w-full m-0 cursor-pointer flex-1 justify-start hover:bg-slate-100"
              variant="ghost"
            >
              {{ connectedAccount.payoutsEnabled ? 'Rediger' : 'Se kontoproblemer' }}
            </Button>
          </PersonOfInterestModal>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <template
        v-if="
          connectedAccount.isDirectPaymentAccount &&
          connectedAccount.directPaymentAccountStatus !== 'Active' &&
          connectedAccount.directPaymentAccountStatus !== 'NotApplicable'
        "
      >
        <DropdownMenuSeparator />
        <DropdownMenuGroup
          :class="{
            'text-red-500 font-semibold': !connectedAccount.directPaymentAccountStatus,
          }"
        >
          <DropdownMenuItem class="p-0">
            <DirectPaymentAccountOnboardingModal
              :businessName="connectedAccount.businessName"
              :organizationId="connectedAccount.organizationId"
              :countryCode="connectedAccount.countryCode"
              :bban="connectedAccount.bban"
              :contactPostalCode="connectedAccount.individualZipcode"
              :contactPersonName="
                connectedAccount.individualFirstname + ' ' + connectedAccount.individualLastname
              "
              :contactPersonEmail="connectedAccount.individualEmail"
            >
              <Button
                @click.prevent.stop
                class="p-2 rounded-sm w-full m-0 cursor-pointer flex-1 justify-start hover:bg-slate-100"
                variant="ghost"
              >
                Last ned direktekonto-avtale
              </Button>
            </DirectPaymentAccountOnboardingModal>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </template>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem
          v-if="
            connectedAccount.netsOcrAgreementOptionActivated &&
            pejer.serverOptions.value?.showActivateDirectRemittanceOption === true
          "
          class="p-0"
        >
          <DirectRemittanceUpgradeModal :connectedAccount="connectedAccount">
            <Button
              @click.prevent.stop
              class="p-2 rounded-sm w-full m-0 cursor-pointer flex-1 justify-start hover:bg-slate-100"
              variant="ghost"
              :disabled="connectedAccount.awaitingNetsOCRAgreement"
            >
              {{
                !connectedAccount.awaitingNetsOCRAgreement
                  ? 'Oppgrader til Direkte betalings-avtale'
                  : 'Venter på OCR-avtale'
              }}
            </Button>
          </DirectRemittanceUpgradeModal>
        </DropdownMenuItem>
        <DropdownMenuItem class="p-0">
          <ArchiveAccountModal :connected-account="connectedAccount">
            <Button
              variant="ghost"
              class="p-2 rounded-sm w-full m-0 cursor-pointer flex-1 flex-col items-start justify-start hover:bg-slate-100 text-red-500 h-fit"
              :disabled="connectedAccount.isArchived || connectedAccount.currentBalance > 0"
            >
              <p>Arkiver konto</p>
              <p
                class="text-xs"
                v-if="connectedAccount.isArchived || connectedAccount.currentBalance > 0"
              >
                ({{
                  connectedAccount.currentBalance > 0
                    ? 'Kontoen har saldo'
                    : 'Kontoen er allerede arkivert'
                }})
              </p>
            </Button>
          </ArchiveAccountModal>
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
